/**
 * Back in stock for the featured product section — via Klaviyo's own modal.
 *
 * Klaviyo's Back in Stock module only initialises on product detail pages: on
 * the home page there is nothing to bind to, so neither `klaviyo.openForm()`
 * nor a `klaviyo-bis-trigger` element does anything. Rather than rebuild the
 * form ourselves, the sold-out button in a `<featured-product-information>`
 * section links through to the product page with that variant preselected and
 * a `notify=1` flag; on arrival we click Klaviyo's own button so the customer
 * lands on the real "Don't miss the restock" modal.
 *
 * Two jobs, one file:
 *   1. Any page with a featured product section — swap the dead "Sold out"
 *      button for a "Sold out - notify me when available" link.
 *   2. Product page opened with `notify=1` — auto-open Klaviyo's modal.
 */

const SCOPE = 'featured-product-information';
const NOTIFY_PARAM = 'notify';
const TRIGGER_TEXT = 'Sold out - notify me when available';

/** Klaviyo tags its injected button with this; stable across wording changes. */
const KLAVIYO_BIS_BUTTON = 'button[data-a11y-identifier^="bis-button-"]';

/** How long to wait for Klaviyo's script to inject its button before giving up. */
const KLAVIYO_WAIT_MS = 12000;

/* ------------------------------------------------------------------ *
 * 1. Featured product section: link sold-out variants to the PDP
 * ------------------------------------------------------------------ */

/**
 * @param {Element} section
 */
function render(section) {
  // Anchor on the button, not the form: the first cart form in the section is
  // Shop Pay's hidden installments form, which has a variant input but no button.
  const soldOutButton = /** @type {HTMLButtonElement | null} */ (
    section.querySelector('form[action*="/cart/add"] button[name="add"]')
  );
  const form = soldOutButton?.closest('form[action*="/cart/add"]');
  const variantId = form?.querySelector('[name="id"]')?.value;
  const productUrl = /** @type {HTMLElement} */ (section).dataset.productUrl;
  if (!soldOutButton || !variantId || !productUrl) return;

  const existing = section.querySelector('.bis-link');

  if (!soldOutButton.disabled) {
    existing?.remove();
    soldOutButton.hidden = false;
    return;
  }

  // Already correct for this variant. Also what stops the observer re-entering.
  if (existing instanceof HTMLAnchorElement && existing.dataset.variant === variantId) return;

  existing?.remove();
  soldOutButton.hidden = true;

  const link = document.createElement('a');
  link.className = `${soldOutButton.className} bis-link`;
  link.dataset.variant = variantId;
  link.href = `${productUrl}?variant=${encodeURIComponent(variantId)}&${NOTIFY_PARAM}=1`;
  link.textContent = TRIGGER_TEXT;
  soldOutButton.after(link);
}

/**
 * Horizon re-renders the section on every variant change, wiping our link and
 * restoring a fresh button, so re-render on any subtree change.
 * @param {Element} section
 */
function observe(section) {
  render(section);
  new MutationObserver(() => render(section)).observe(section, { childList: true, subtree: true });
}

document.querySelectorAll(SCOPE).forEach(observe);

new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(SCOPE)) observe(node);
      else node.querySelectorAll?.(SCOPE).forEach(observe);
    }
  }
}).observe(document.body, { childList: true, subtree: true });

/* ------------------------------------------------------------------ *
 * 2. Product page: open Klaviyo's modal on arrival
 * ------------------------------------------------------------------ */

if (new URLSearchParams(location.search).get(NOTIFY_PARAM) === '1') {
  // Drop the flag so a refresh or back-navigation doesn't reopen the modal.
  const url = new URL(location.href);
  url.searchParams.delete(NOTIFY_PARAM);
  history.replaceState(history.state, '', url);

  /** @param {Element} button */
  const open = (button) => /** @type {HTMLElement} */ (button).click();

  const present = document.querySelector(KLAVIYO_BIS_BUTTON);
  if (present) {
    open(present);
  } else {
    // Klaviyo injects asynchronously, so wait for it rather than racing it.
    const observer = new MutationObserver(() => {
      const button = document.querySelector(KLAVIYO_BIS_BUTTON);
      if (!button) return;
      observer.disconnect();
      open(button);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), KLAVIYO_WAIT_MS);
  }
}
